import { useState } from "react";
import { toast } from "sonner";
import { Download, Trash2, History, Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/crm";

const ACTIONS = { consulta_cliente: "Consultazione scheda cliente", export_cliente: "Esportazione dati cliente", export_account: "Esportazione account",
  scarica_documento: "Download documento", scarica_pdf_privacy: "Download PDF privacy", elimina_cliente: "Eliminazione cliente" };

export const downloadJson = async (url, filename) => {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(errMsg(e)); }
};

export const AiToggle = ({ f, setF, save }) => {
  const set = (k) => (v) => { const n = { ...f, [k]: v }; setF(n); save(n); };
  return (
    <section className="bg-white border border-slate-200/80 rounded-xl p-6 space-y-5" data-testid="ai-settings">
      <div className="flex items-start justify-between gap-6">
        <div>
          <h3 className="font-display font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-600" />Funzioni di intelligenza artificiale</h3>
          <p className="text-sm text-slate-500 mt-1">Bozze di messaggi, riepiloghi dei clienti, Assistente AI e consiglio del mattino. Se le disattivi, nessun dato dei tuoi clienti viene inviato al fornitore AI. La chat di prenotazione pubblica si gestisce da Prenotazioni.</p>
        </div>
        <Switch data-testid="ai-enabled-switch" checked={f.ai_enabled} onCheckedChange={set("ai_enabled")} />
      </div>
      <div className="flex items-start justify-between gap-6 border-t border-slate-100 pt-5">
        <div>
          <p className="font-medium text-sm">Includi le note di clienti e pratiche</p>
          <p className="text-sm text-slate-500 mt-1">Disattivato: le note non vengono mai inviate all'AI, così eventuali dati sanitari restano fuori. Attivalo solo se nelle note non ci sono dati particolari.</p>
        </div>
        <Switch data-testid="ai-include-notes-switch" checked={!!f.ai_include_notes} disabled={!f.ai_enabled} onCheckedChange={set("ai_include_notes")} />
      </div>
    </section>
  );
};

const AccessLog = ({ open, onOpenChange }) => {
  const [items, setItems] = useState(null);
  const load = (o) => { onOpenChange(o); if (o) api.get("/account/access-log").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))); };
  return (
    <Dialog open={open} onOpenChange={load}>
      <DialogContent className="max-w-lg" data-testid="access-log-dialog">
        <DialogHeader><DialogTitle>Registro accessi (ultimi 90 giorni)</DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100 text-sm">
          {items?.length === 0 && <p className="text-slate-400 py-6 text-center">Nessuna attività registrata.</p>}
          {items?.map((x, i) => (
            <div key={i} className="py-2 flex justify-between gap-3" data-testid={`access-log-row-${i}`}>
              <span>{ACTIONS[x.action] || x.action}</span><span className="font-mono text-xs text-slate-400 shrink-0">{fmtDateTime(x.at)}</span>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DeleteAccount = ({ open, onOpenChange }) => {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await api.delete("/account", { data: { confirm } }); toast.success("Account eliminato"); window.location.href = "/login"; }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="delete-account-dialog">
        <DialogHeader><DialogTitle>Eliminare definitivamente l'account?</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Verranno cancellati per sempre clienti, pratiche, documenti, firme privacy, chat e impostazioni. L'abbonamento sarà disdetto. Scarica prima una copia dei dati. L'operazione non è reversibile.</p>
        <p className="text-sm">Scrivi <b>ELIMINA</b> per confermare:</p>
        <Input data-testid="delete-account-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        <Button data-testid="delete-account-confirm-btn" disabled={confirm.trim().toUpperCase() !== "ELIMINA" || busy} onClick={run} className="bg-rose-600 hover:bg-rose-700">Elimina account</Button>
      </DialogContent>
    </Dialog>
  );
};

export const AccountData = () => {
  const [log, setLog] = useState(false);
  const [del, setDel] = useState(false);
  return (
    <section className="bg-white border border-slate-200/80 rounded-xl p-6 space-y-4" data-testid="account-data-section">
      <h3 className="font-display font-semibold">Privacy e dati del tuo account</h3>
      <p className="text-sm text-slate-500">Conservazione automatica: sessioni e registri 90 giorni, chat AI 12 mesi, account con abbonamento non attivo eliminato dopo 12 mesi (avviso via email 30 giorni prima). Documenti e firme restano fino all'eliminazione del cliente.</p>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="export-account-btn" variant="outline" onClick={() => downloadJson("/account/export", "kreditofacile_export.json")}><Download className="h-4 w-4 mr-2" />Esporta tutti i miei dati</Button>
        <Button data-testid="access-log-btn" variant="outline" onClick={() => setLog(true)}><History className="h-4 w-4 mr-2" />Registro accessi</Button>
        <Button data-testid="delete-account-btn" variant="outline" onClick={() => setDel(true)} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 className="h-4 w-4 mr-2" />Elimina account</Button>
      </div>
      <AccessLog open={log} onOpenChange={setLog} />
      <DeleteAccount open={del} onOpenChange={setDel} />
    </section>
  );
};
